import type React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Mail,
  ChevronDown
} from "lucide-react";
import { PRODUCT_CATEGORY_STRUCTURE } from "../data/ProductCategories";

const Footer: React.FC = () => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [expandedSubcategory, setExpandedSubcategory] = useState<string | null>(null);

  const toggleCategory = (category: string) => {
    if (expandedCategory === category) {
      setExpandedCategory(null);
      setExpandedSubcategory(null);
    } else {
      setExpandedCategory(category);
      setExpandedSubcategory(null);
    }
  };

  const toggleSubcategory = (subcategory: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (expandedSubcategory === subcategory) {
      setExpandedSubcategory(null);
    } else {
      setExpandedSubcategory(subcategory);
    }
  };

  const getSubcategories = (category: string) =>
    PRODUCT_CATEGORY_STRUCTURE[category]?.subcategories || {};

  const getSubcategoryTypes = (category: string, subcategory: string): string[] => {
    const subcategories = getSubcategories(category);
    return subcategories[subcategory] || [];
  };

  // Check if a subcategory has types
  const hasTypes = (category: string, subcategory: string) => {
    return getSubcategoryTypes(category, subcategory).length > 0;
  };

  return (
    <footer>
      <div className="bg-gray-100 py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div>
              <img
                src="/images/logo/logo.png"
                alt="PES Lube Canada"
                className="h-12 mb-4"
              />
              <p className="text-gray-600 mb-4">
                PES is the official distributor for Sinopec Lubricants in Canada, supplying Sinopec products directly to Canadian customers. Contact PES for pricing, availability, product selection, and technical documentation.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4 relative inline-block">
                Quick Links
                <span
                  className="absolute bottom-0 left-0 w-12 h-1 bg-red-600"
                  style={{ marginBottom: "-6px" }}
                ></span>
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/products"
                    className="text-gray-600 hover:text-red-600 transition-colors"
                  >
                    Products
                  </Link>
                </li>
                <li>
                  <Link
                    to="/equivalent-guide"
                    className="text-gray-600 hover:text-red-600 transition-colors"
                  >
                    Equivalent Guide
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-gray-600 hover:text-red-600 transition-colors"
                  >
                    About Sinopec
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-gray-600 hover:text-red-600 transition-colors"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Products */}
            <div>
              <h3 className="text-lg font-semibold mb-4 relative inline-block">
                Products
                <span
                  className="absolute bottom-0 left-0 w-12 h-1 bg-red-600"
                  style={{ marginBottom: "-6px" }}
                ></span>
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/products"
                    className="text-gray-600 hover:text-red-600 transition-colors font-medium"
                  >
                    All Products
                  </Link>
                </li>
                
                {Object.keys(PRODUCT_CATEGORY_STRUCTURE).map((category) => (
                  <li key={category}>
                    <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleCategory(category)}>
                      <Link
                        to="/products"
                        className="text-gray-600 hover:text-red-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.localStorage.setItem("productCategory", category);
                        }}
                      >
                        {category}
                      </Link>
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${expandedCategory === category ? 'rotate-180' : ''}`}
                      />
                    </div>
                    {expandedCategory === category && (
                      <ul className="pl-4 mt-2 space-y-1 border-l border-gray-200">
                        {Object.keys(getSubcategories(category)).map((subcategory) => (
                          <li key={subcategory}>
                            <div className="flex items-center justify-between">
                              <Link
                                to="/products"
                                className="text-gray-500 hover:text-red-600 transition-colors text-sm"
                                onClick={() => {
                                  window.localStorage.setItem("productCategory", category);
                                  window.localStorage.setItem("productSubcategory", subcategory);
                                }}
                              >
                                {subcategory}
                              </Link>
                              {hasTypes(category, subcategory) && (
                                <ChevronDown
                                  size={14}
                                  className={`transition-transform cursor-pointer ${expandedSubcategory === subcategory ? 'rotate-180' : ''}`}
                                  onClick={(e) => toggleSubcategory(subcategory, e)}
                                />
                              )}
                            </div>
                            {expandedSubcategory === subcategory && hasTypes(category, subcategory) && (
                              <ul className="pl-4 mt-1 space-y-1 border-l border-gray-200">
                                {getSubcategoryTypes(category, subcategory).map((type: string) => (
                                  <li key={type}>
                                    <Link
                                      to="/products"
                                      className="text-gray-500 hover:text-red-600 transition-colors text-xs"
                                      onClick={() => {
                                        window.localStorage.setItem("productCategory", category);
                                        window.localStorage.setItem("productSubcategory", subcategory);
                                        window.localStorage.setItem("productProduct", type);
                                      }}
                                    >
                                      {type}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold mb-4 relative inline-block">
                Contact PES
                <span
                  className="absolute bottom-0 left-0 w-12 h-1 bg-red-600"
                  style={{ marginBottom: "-6px" }}
                ></span>
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <MapPin
                    size={20}
                    className="mr-2 text-red-600 flex-shrink-0 mt-1"
                  />
                  <span className="text-gray-600">
                    Petroland Equipment Solutions Ltd.
                  </span>
                </li>
                <li className="flex items-center">
                  <Mail size={20} className="mr-2 text-red-600 flex-shrink-0" />
                  <a href="mailto:sales@petrolandesl.com" className="text-gray-600 hover:text-red-600 transition-colors">
                    sales@petrolandesl.com
                  </a>
                </li>
                <li className="flex items-center">
                  <span className="mr-2 text-red-600 flex-shrink-0 font-semibold">
                    Tel
                  </span>
                  <a href="tel:+14037087336" className="text-gray-600 hover:text-red-600 transition-colors">
                    +1 403-708-7336
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="bg-red-600 py-4 text-white">
        <div className="container mx-auto px-4 text-center">
          <p>
            &copy; {new Date().getFullYear()} PES Lube Canada. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
